/**
 * FullscreenButton — overlay primitive that expands the surrounding player
 * surface into native fullscreen. Hosts can drop it into the
 * `player_overlay` slot of any shell.
 *
 * The button walks up from itself to find the nearest element marked with
 * `data-fullscreen-root` (set by the shell on the surface that should fill
 * the screen, including any chrome that must stay reachable) and
 * sends that to {@link requestPlayerFullscreen}. The helper tries the
 * <video> tag first (required for iOS Safari) before falling back to the
 * wrapper element.
 *
 * Renders as an icon-only round button, top-right of the player by
 * default. Position is owned by the shell — this primitive is just the
 * pill itself.
 */

import { useRef } from 'react'
import { Maximize2 } from 'lucide-react'
import { findPlayerSurface, requestPlayerFullscreen } from '../../lib/fullscreen'
import { registerPrimitive } from '../../layout'

interface FullscreenButtonConfig {
  /** aria-label for the button. Defaults to "Open fullscreen". */
  label?: string
}

export function FullscreenButton({ config }: { id?: string; config: Record<string, unknown> }) {
  const cfg = config as FullscreenButtonConfig
  const ref = useRef<HTMLButtonElement | null>(null)
  return (
    <button
      ref={ref}
      type="button"
      className="lv-player-ctrl"
      aria-label={cfg.label ?? 'Open fullscreen'}
      title={cfg.label ?? 'Fullscreen'}
      onClick={(e) => {
        e.stopPropagation()
        requestPlayerFullscreen(findPlayerSurface(ref.current))
      }}
    >
      <Maximize2 size={16} strokeWidth={2} aria-hidden="true" />
    </button>
  )
}

registerPrimitive('FullscreenButton', FullscreenButton)
